/* ============================================================
 * AYU /erleben – "Erlebe AYU. Ganz ohne Druck."
 * Interactive demo session page – analog to MENTO /erleben
 * Design: Gold + Olive Sacred Modern
 * ============================================================ */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { IMAGES } from "@/lib/constants";
import { useI18n } from "@/contexts/I18nContext";
import {
  Leaf,
  Brain,
  Sparkles,
  Sun,
  Heart,
  ChevronRight,
  Send,
  SkipForward,
  ArrowRight,
} from "lucide-react";

/* ---------- Topic Data ---------- */
interface ChatMessage {
  sender: "system" | "user" | "ayu" | "insight" | "summary";
  label?: string;
  text: string;
}

interface Topic {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  messages: ChatMessage[];
}

type TopicMsgDef = {
  sender: "system" | "user" | "ayu" | "insight" | "summary";
  label?: string;
  labelKey?: string;
  textKey: string;
};

type TopicDef = {
  id: string;
  titleKey: string;
  subtitleKey: string;
  icon: React.ReactNode;
  messages: TopicMsgDef[];
};

const TOPIC_DEFS: TopicDef[] = [
  {
    id: "longevity",
    titleKey: "erleben.topics.t1Title",
    subtitleKey: "erleben.topics.t1Subtitle",
    icon: <Sun className="w-5 h-5" />,
    messages: [
      { sender: "system", textKey: "erleben.topics.t1M1" },
      { sender: "ayu", label: "AYU", textKey: "erleben.topics.t1M2" },
      { sender: "user", textKey: "erleben.topics.t1M3" },
      { sender: "ayu", label: "AYU", textKey: "erleben.topics.t1M4" },
      { sender: "user", textKey: "erleben.topics.t1M5" },
      { sender: "insight", labelKey: "erleben.topics.t1M6Label", textKey: "erleben.topics.t1M6" },
      { sender: "system", textKey: "erleben.topics.t1M7" },
      { sender: "summary", labelKey: "erleben.topics.t1M8Label", textKey: "erleben.topics.t1M8" },
    ],
  },
  {
    id: "darm",
    titleKey: "erleben.topics.t2Title",
    subtitleKey: "erleben.topics.t2Subtitle",
    icon: <Heart className="w-5 h-5" />,
    messages: [
      { sender: "system", textKey: "erleben.topics.t2M1" },
      { sender: "ayu", label: "AYU", textKey: "erleben.topics.t2M2" },
      { sender: "user", textKey: "erleben.topics.t2M3" },
      { sender: "ayu", label: "AYU", textKey: "erleben.topics.t2M4" },
      { sender: "user", textKey: "erleben.topics.t2M5" },
      { sender: "insight", labelKey: "erleben.topics.t2M6Label", textKey: "erleben.topics.t2M6" },
      { sender: "system", textKey: "erleben.topics.t2M7" },
      { sender: "summary", labelKey: "erleben.topics.t2M8Label", textKey: "erleben.topics.t2M8" },
    ],
  },
  {
    id: "adaptogene",
    titleKey: "erleben.topics.t3Title",
    subtitleKey: "erleben.topics.t3Subtitle",
    icon: <Leaf className="w-5 h-5" />,
    messages: [
      { sender: "system", textKey: "erleben.topics.t3M1" },
      { sender: "ayu", label: "AYU", textKey: "erleben.topics.t3M2" },
      { sender: "user", textKey: "erleben.topics.t3M3" },
      { sender: "ayu", label: "AYU", textKey: "erleben.topics.t3M4" },
      { sender: "user", textKey: "erleben.topics.t3M5" },
      { sender: "insight", labelKey: "erleben.topics.t3M6Label", textKey: "erleben.topics.t3M6" },
      { sender: "system", textKey: "erleben.topics.t3M7" },
      { sender: "summary", labelKey: "erleben.topics.t3M8Label", textKey: "erleben.topics.t3M8" },
    ],
  },
];

/* ---------- Typing Indicator ---------- */
function TypingIndicator() {
  return (
    <div className="flex items-center gap-1.5 px-4 py-3">
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2 h-2 rounded-full bg-olive"
            animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1, 0.8] }}
            transition={{
              duration: 1.2,
              repeat: Infinity,
              delay: i * 0.2,
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* ---------- Chat Message Component ---------- */
function ChatBubble({ message }: { message: ChatMessage }) {
  if (message.sender === "system") {
    return (
      <div className="flex justify-center my-4">
        <span className="text-xs text-earth-light/60 tracking-wider uppercase">
          {message.text}
        </span>
      </div>
    );
  }

  if (message.sender === "user") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-end mb-3"
      >
        <div className="bg-olive text-ivory rounded-2xl rounded-br-sm px-4 py-3 max-w-[85%] text-sm leading-relaxed">
          {message.text}
        </div>
      </motion.div>
    );
  }

  if (message.sender === "insight") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-3"
      >
        <div className="bg-gold/10 border border-gold/30 rounded-2xl rounded-bl-sm px-4 py-3 max-w-[90%]">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Sparkles className="w-3 h-3 text-gold" />
            <span className="text-xs font-semibold text-gold tracking-wider uppercase">
              {message.label}
            </span>
          </div>
          <p className="text-sm text-earth leading-relaxed">{message.text}</p>
        </div>
      </motion.div>
    );
  }

  if (message.sender === "summary") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-3"
      >
        <div className="bg-olive/10 border border-olive/30 rounded-2xl rounded-bl-sm px-4 py-3 max-w-[90%]">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Brain className="w-3 h-3 text-olive" />
            <span className="text-xs font-semibold text-olive tracking-wider uppercase">
              {message.label}
            </span>
          </div>
          <p className="text-sm text-earth leading-relaxed">{message.text}</p>
        </div>
      </motion.div>
    );
  }

  // AYU message
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-3"
    >
      <div className="bg-ivory-dark/80 rounded-2xl rounded-bl-sm px-4 py-3 max-w-[90%]">
        <div className="flex items-center gap-1.5 mb-1">
          <div className="w-1.5 h-1.5 rounded-full bg-olive" />
          <span className="text-xs font-semibold text-olive tracking-wider uppercase">
            {message.label}
          </span>
        </div>
        <p className="text-sm text-earth leading-relaxed">{message.text}</p>
      </div>
    </motion.div>
  );
}

/* ---------- Main Erleben Page ---------- */
export default function Erleben() {
  const { t } = useI18n();

  const [selectedTopic, setSelectedTopic] = useState<string>("longevity");
  const [visibleMessages, setVisibleMessages] = useState<number>(0);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const TOPICS: Topic[] = useMemo(
    () =>
      TOPIC_DEFS.map((def) => ({
        id: def.id,
        title: t(def.titleKey as any),
        subtitle: t(def.subtitleKey as any),
        icon: def.icon,
        messages: def.messages.map((msg) => ({
          sender: msg.sender,
          label: msg.labelKey ? t(msg.labelKey as any) : msg.label,
          text: t(msg.textKey as any),
        })),
      })),
    [t]
  );

  const currentTopic = TOPICS.find((topic) => topic.id === selectedTopic)!;
  const totalMessages = currentTopic.messages.length;
  const progress = Math.round((visibleMessages / totalMessages) * 100);

  // Auto-scroll chat
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [visibleMessages, isTyping]);

  // Auto-advance messages
  const advanceMessage = useCallback(() => {
    if (visibleMessages >= totalMessages) {
      setSessionComplete(true);
      setIsTyping(false);
      return;
    }

    setIsTyping(true);

    const nextMsg = currentTopic.messages[visibleMessages];
    const delay =
      nextMsg.sender === "system"
        ? 800
        : nextMsg.sender === "user"
        ? 1500
        : nextMsg.sender === "insight" || nextMsg.sender === "summary"
        ? 2500
        : 2000;

    timerRef.current = setTimeout(() => {
      setIsTyping(false);
      setVisibleMessages((prev) => prev + 1);
    }, delay);
  }, [visibleMessages, totalMessages, currentTopic]);

  useEffect(() => {
    if (visibleMessages < totalMessages && !sessionComplete) {
      const autoTimer = setTimeout(advanceMessage, 600);
      return () => clearTimeout(autoTimer);
    }
    if (visibleMessages >= totalMessages) {
      setSessionComplete(true);
    }
  }, [visibleMessages, totalMessages, advanceMessage, sessionComplete]);

  // Reset on topic change
  const handleTopicChange = (topicId: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setSelectedTopic(topicId);
    setVisibleMessages(0);
    setIsTyping(false);
    setSessionComplete(false);
  };

  // Skip to next message
  const handleSkip = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsTyping(false);
    if (visibleMessages < totalMessages) {
      setVisibleMessages((prev) => prev + 1);
    }
  };

  const STEPS = [
    {
      num: "01",
      icon: <Heart className="w-6 h-6" />,
      titleKey: "erleben.method.step1Title",
      descKey: "erleben.method.step1Desc",
    },
    {
      num: "02",
      icon: <Brain className="w-6 h-6" />,
      titleKey: "erleben.method.step2Title",
      descKey: "erleben.method.step2Desc",
    },
    {
      num: "03",
      icon: <Sparkles className="w-6 h-6" />,
      titleKey: "erleben.method.step3Title",
      descKey: "erleben.method.step3Desc",
    },
  ];

  const STATS = [
    { valueKey: "erleben.stats.stat1Value", labelKey: "erleben.stats.stat1Label" },
    { valueKey: "erleben.stats.stat2Value", labelKey: "erleben.stats.stat2Label" },
    { valueKey: "erleben.stats.stat3Value", labelKey: "erleben.stats.stat3Label" },
  ];

  return (
    <div className="min-h-screen bg-midnight text-ivory">
      {/* Mandala overlay */}
      <div className="fixed inset-0 mandala-overlay pointer-events-none" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-midnight/90 backdrop-blur-md border-b border-gold/10">
        <div className="container flex items-center justify-between h-16">
          <Link href="/">
            <img src={IMAGES.logo} alt="AYU" className="h-10" />
          </Link>
          <Link
            href="/funnel"
            className="btn-olive px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2"
          >
            {t("landing.nav.jetztStarten")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-16 md:py-24">
        <div className="container text-center">
          <span className="inline-block text-gold text-xs tracking-[0.3em] uppercase font-semibold mb-4">
            {t("erleben.hero.badge")}
          </span>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t("erleben.hero.heading1")}
            <br />
            <span className="text-olive-light italic">{t("erleben.hero.heading2")}</span>
          </h1>
          <p className="text-ivory/60 text-lg max-w-xl mx-auto">
            {t("erleben.hero.description")}
          </p>
        </div>
      </section>

      {/* Topic Selection */}
      <section className="pb-8">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {TOPICS.map((topic) => (
              <button
                key={topic.id}
                onClick={() => handleTopicChange(topic.id)}
                className={`flex items-center gap-3 px-5 py-4 rounded-xl border transition-all duration-300 text-left ${
                  selectedTopic === topic.id
                    ? "border-olive bg-olive/15 text-ivory"
                    : "border-gold/20 bg-midnight-light/50 text-ivory/60 hover:border-gold/40 hover:bg-midnight-light"
                }`}
              >
                <div
                  className={`p-2 rounded-lg ${
                    selectedTopic === topic.id
                      ? "bg-olive text-ivory"
                      : "bg-gold/10 text-gold"
                  }`}
                >
                  {topic.icon}
                </div>
                <div>
                  <div className="font-semibold text-sm">{topic.title}</div>
                  <div className="text-xs opacity-60">{topic.subtitle}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Chat Demo */}
      <section className="pb-16">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            {/* Chat Window */}
            <div className="bg-ivory rounded-2xl shadow-2xl overflow-hidden">
              {/* Chat Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-earth/10">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-olive/15 flex items-center justify-center">
                    <Leaf className="w-4 h-4 text-olive" />
                  </div>
                  <div>
                    <div className="font-semibold text-earth text-sm">
                      {t("erleben.chat.healthLabel")} — {currentTopic.title}
                    </div>
                    <div className="text-xs text-olive">
                      {sessionComplete
                        ? t("erleben.chat.sessionComplete")
                        : t("erleben.chat.sessionActive")}
                    </div>
                  </div>
                </div>
                {!sessionComplete && (
                  <button
                    onClick={handleSkip}
                    className="flex items-center gap-1 text-xs text-earth-light/50 hover:text-olive transition-colors px-3 py-1.5 rounded-lg border border-earth/10 hover:border-olive/30"
                  >
                    <SkipForward className="w-3 h-3" />
                    {t("erleben.chat.skipButton")}
                  </button>
                )}
              </div>

              {/* Chat Messages */}
              <div
                ref={chatRef}
                className="px-5 py-4 overflow-y-auto"
                style={{
                  minHeight: "380px",
                  maxHeight: "480px",
                }}
              >
                <AnimatePresence mode="sync">
                  {currentTopic.messages
                    .slice(0, visibleMessages)
                    .map((msg, i) => (
                      <ChatBubble key={`${selectedTopic}-${i}`} message={msg} />
                    ))}
                </AnimatePresence>

                {isTyping && <TypingIndicator />}

                {sessionComplete && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center mt-6 text-xs text-earth-light/50"
                  >
                    {t("erleben.chat.completionText")}
                  </motion.div>
                )}
              </div>

              {/* Input Bar */}
              <div className="px-5 py-3 border-t border-earth/10">
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-ivory-dark rounded-xl px-4 py-2.5 text-sm text-earth-light/40">
                    {t("erleben.chat.inputPlaceholder")}
                  </div>
                  <div className="w-9 h-9 rounded-xl bg-olive/20 flex items-center justify-center">
                    <Send className="w-4 h-4 text-olive" />
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="px-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-1.5 bg-earth/10 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-olive rounded-full"
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <span className="text-xs text-earth-light/40 min-w-[3rem] text-right">
                    {progress}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3-Step Method */}
      <section className="py-20 bg-ivory text-earth">
        <div className="container text-center">
          <span className="text-olive text-xs tracking-[0.3em] uppercase font-semibold">
            {t("erleben.method.badge")}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold mt-3 mb-12"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t("erleben.method.heading1")}{" "}
            <span className="text-olive italic">{t("erleben.method.heading2")}</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {STEPS.map((step) => (
              <div
                key={step.num}
                className="bg-white rounded-2xl p-8 border border-earth/10 shadow-sm"
              >
                <div className="text-gold text-2xl font-bold mb-3">
                  {step.num}
                </div>
                <div className="w-12 h-12 rounded-xl bg-olive/10 flex items-center justify-center text-olive mx-auto mb-4">
                  {step.icon}
                </div>
                <h3
                  className="text-lg font-bold mb-2"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {t(step.titleKey as any)}
                </h3>
                <p className="text-sm text-earth-light leading-relaxed">
                  {t(step.descKey as any)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-16 bg-ivory-dark text-earth">
        <div className="container text-center">
          <span className="text-olive text-xs tracking-[0.3em] uppercase font-semibold">
            {t("erleben.stats.badge")}
          </span>
          <h2
            className="text-2xl md:text-3xl font-bold mt-3 mb-10 max-w-2xl mx-auto"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t("erleben.stats.heading")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto mb-6">
            {STATS.map((stat) => (
              <div key={stat.labelKey}>
                <div
                  className="text-3xl md:text-4xl font-bold text-gold mb-1"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {t(stat.valueKey as any)}
                </div>
                <div className="text-sm text-earth-light">{t(stat.labelKey as any)}</div>
              </div>
            ))}
          </div>

          <p className="text-xs text-earth-light/50">
            {t("erleben.stats.source")}
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-midnight text-ivory relative">
        <div className="absolute inset-0 mandala-overlay pointer-events-none" />
        <div className="container text-center relative z-10">
          <div className="w-14 h-14 rounded-2xl bg-olive/20 flex items-center justify-center mx-auto mb-6">
            <Leaf className="w-7 h-7 text-olive-light" />
          </div>
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t("erleben.cta.heading1")}
            <br />
            <span className="text-olive-light italic">{t("erleben.cta.heading2")}</span>
          </h2>
          <p className="text-ivory/60 max-w-lg mx-auto mb-8 leading-relaxed">
            {t("erleben.cta.description")}
          </p>
          <Link
            href="/funnel"
            className="inline-flex items-center gap-2 btn-olive px-8 py-4 rounded-xl text-base font-semibold"
          >
            {t("erleben.cta.button")}
            <ChevronRight className="w-5 h-5" />
          </Link>
          <div className="flex items-center justify-center gap-6 mt-5 text-xs text-ivory/40">
            <span className="flex items-center gap-1.5">
              <div className="w-1 h-1 rounded-full bg-olive" />
              {t("erleben.cta.time")}
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-1 h-1 rounded-full bg-olive" />
              {t("erleben.cta.free")}
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-1 h-1 rounded-full bg-olive" />
              {t("erleben.cta.noObligation")}
            </span>
          </div>

          <div className="mt-8 text-ivory/30 text-sm">
            {t("erleben.cta.email")}{" "}
            <a
              href="mailto:office@ayu-rveda.com"
              className="text-gold hover:text-gold-light underline underline-offset-2"
            >
              office@ayu-rveda.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-midnight border-t border-gold/10">
        <div className="container flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="AYU" className="h-8 opacity-60" />
            <span className="text-ivory/30 text-sm">
              &copy; {new Date().getFullYear()} {t("landing.footer.copyright")}
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-ivory/40">
            <Link href="/impressum" className="hover:text-gold transition-colors">
              {t("landing.footer.impressum")}
            </Link>
            <Link href="/datenschutz" className="hover:text-gold transition-colors">
              {t("landing.footer.datenschutz")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
